'use client'

import { EASE } from '@/animations/settings/vars'
import { cn } from '@/lib/utils'
import { Typo3SectionSpotHeadlessMap } from '@/types/typo3'
import {
  Button,
  ButtonIcon,
  Container,
  Img,
  Section,
  Text,
  Title
} from '@/ui/components'
import { gsap } from '@gsap'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SectionMapMarker from './SectionMapMarker'
import { REGIONS } from './regions'

interface Props {
  data: Typo3SectionSpotHeadlessMap
}

const SectionMap = ({ data }: Props) => {
  const [currentRegionIndex, setCurrentRegionIndex] = useState<number>(0)
  const [prevRegionIndex, setPrevRegionIndex] = useState<number>(-1)
  const [slideCount] = useState<number>(data.content.items?.length ?? 0)
  const imageSlidesRef = useRef<HTMLDivElement>(null)
  const contentSlidesRef = useRef<HTMLDivElement>(null)

  const [isOver, setIsOver] = useState<number | null>(null)

  const activeRegionsArray = useMemo(
    () => data.content.items?.map((item) => item.region),
    [data.content.items]
  )

  useEffect(() => {
    // randomize first region
    if (slideCount > 0) {
      const randomRegionIndex = Math.floor(Math.random() * slideCount) + 1
      setTimeout(() => {
        setCurrentRegionIndex(randomRegionIndex)
      }, 100)
    }

    if (!imageSlidesRef.current || slideCount === 0) {
      return
    }

    const slides = gsap.utils.toArray<HTMLElement>(
      '[data-map-image-slide]',
      imageSlidesRef.current
    )
    const initialSlide = slides[0]
    const contentSlides = contentSlidesRef.current
      ? gsap.utils.toArray<HTMLElement>(
          '[data-map-content-slide]',
          contentSlidesRef.current
        )
      : []
    const initialContentSlide = contentSlides[0]

    gsap.set(slides, { scale: 1, autoAlpha: 0 })
    if (initialSlide) {
      gsap.set(initialSlide, { scale: 1, autoAlpha: 1 })
    }

    gsap.set(contentSlides, { autoAlpha: 0 })
    if (initialContentSlide) {
      const initialElements = gsap.utils.toArray<HTMLElement>(
        '[data-map-content-el]',
        initialContentSlide
      )
      gsap.set(initialContentSlide, { autoAlpha: 1 })
      gsap.set(initialElements, { autoAlpha: 0 })
    }
  }, [slideCount])

  useEffect(() => {
    if (
      !imageSlidesRef.current ||
      !contentSlidesRef.current ||
      slideCount <= 1
    ) {
      return
    }

    const slides = gsap.utils.toArray<HTMLElement>(
      '[data-map-image-slide]',
      imageSlidesRef.current
    )
    const contentSlides = gsap.utils.toArray<HTMLElement>(
      '[data-map-content-slide]',
      contentSlidesRef.current
    )

    const currentSlide = slides[currentRegionIndex - 1]
    const previousSlide = slides[prevRegionIndex - 1]
    const currentContentSlide = contentSlides[currentRegionIndex - 1]
    const previousContentSlide = contentSlides[prevRegionIndex - 1]

    if (
      !currentSlide ||
      !currentContentSlide ||
      previousSlide === currentSlide
    ) {
      return
    }

    const isForward =
      (prevRegionIndex === slideCount && currentRegionIndex === 1) ||
      (currentRegionIndex > prevRegionIndex &&
        !(prevRegionIndex === 1 && currentRegionIndex === slideCount))

    const previousTargetScale = isForward ? 2 : 1
    const currentStartScale = isForward ? 1 : 2
    const previousContentX = isForward ? -20 : 20
    const currentContentX = isForward ? 30 : -30
    const currentEndScale = 1.1
    const previousContentElements = previousContentSlide
      ? gsap.utils.toArray<HTMLElement>(
          '[data-map-content-el]',
          previousContentSlide
        )
      : []
    const currentContentElements = gsap.utils.toArray<HTMLElement>(
      '[data-map-content-el]',
      currentContentSlide
    )

    gsap.killTweensOf([
      previousSlide,
      currentSlide,
      previousContentSlide,
      currentContentSlide,
      ...previousContentElements,
      ...currentContentElements
    ])
    if (previousSlide) {
      gsap.set(previousSlide, { zIndex: 3 })
    }
    gsap.set(currentSlide, {
      scale: currentStartScale,
      autoAlpha: 0,
      zIndex: 2
    })
    if (previousContentSlide) {
      gsap.set(previousContentSlide, { autoAlpha: 1, zIndex: 3 })
    }
    gsap.set(currentContentSlide, { autoAlpha: 1, zIndex: 4 })
    gsap.set(currentContentElements, { autoAlpha: 0, x: currentContentX })

    const tl = gsap.timeline()
    if (previousSlide) {
      tl.to(previousSlide, {
        scale: previousTargetScale,
        autoAlpha: 0,
        duration: 1.2,
        ease: EASE.expoInOut
      })
    }

    if (previousContentElements.length > 0) {
      tl.to(
        previousContentElements,
        {
          autoAlpha: 0,
          x: previousContentX,
          duration: 0.35,
          stagger: 0.04,
          ease: EASE.in
        },
        '<'
      )
    }
    tl.to(
      currentSlide,
      {
        scale: currentEndScale,
        autoAlpha: 1,
        duration: 1.2,
        ease: EASE.expoOut
      },
      '<+=0.6'
    )
    if (previousContentSlide) {
      tl.set(previousContentSlide, { autoAlpha: 0 })
    }
    if (currentContentElements.length > 0) {
      tl.to(
        currentContentElements,
        {
          autoAlpha: 1,
          x: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: EASE.out
        },
        '<-=1'
      )
    }
  }, [currentRegionIndex, prevRegionIndex, slideCount])

  const scrollPrev = useCallback(() => {
    if (slideCount === 0) {
      return
    }
    const prevRegionIndex =
      currentRegionIndex - 1 <= 0 ? slideCount : currentRegionIndex - 1
    setPrevRegionIndex(currentRegionIndex)
    setCurrentRegionIndex(prevRegionIndex)
  }, [currentRegionIndex, slideCount])

  const scrollNext = useCallback(() => {
    if (slideCount === 0) {
      return
    }
    const nextRegionIndex =
      currentRegionIndex + 1 > slideCount ? 1 : currentRegionIndex + 1
    setPrevRegionIndex(currentRegionIndex)
    setCurrentRegionIndex(nextRegionIndex)
  }, [currentRegionIndex, slideCount])

  const handleMouseEnter = useCallback((index: number) => {
    setIsOver(index)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsOver(null)
  }, [])

  return (
    <>
      <Section data={data} className="s-section-map relative overflow-hidden">
        <div className="relative">
          <ButtonIcon
            onClick={scrollPrev}
            icon="material-symbols:arrow-back-ios-new"
            className="left-sm max-md:bottom-sm absolute z-3 cursor-pointer md:top-2/3 md:-translate-y-1/2 lg:top-1/2"
            size="m"
            aria-label="Previous slide"
          />
          <div
            ref={contentSlidesRef}
            className={cn('max-md:aspect-rect relative z-2 aspect-video')}
          >
            {data.content.items?.map((item, index) => {
              const region = REGIONS.find(
                (region) => region.id === Number(item.region)
              )
              return (
                <div
                  className={cn(
                    'group/slider pointer-events-none absolute inset-0 translate-0 pl-0',
                    currentRegionIndex === index + 1 && 'pointer-events-auto'
                  )}
                  data-map-content-slide
                  key={index?.toString()}
                >
                  <div className="absolute inset-0 text-neutral-100">
                    <Container
                      size="md"
                      className="gap-gutter lg:max-2xl:mx-xl py-2xl relative z-15 flex h-full flex-col items-center justify-center lg:grid lg:grid-cols-2"
                    >
                      <div className="w-full">
                        <div
                          className={cn('h5 gap-xs mb-xs flex items-center')}
                          data-map-content-el
                        >
                          <span className="circle textstyle-sm-bold inline-flex size-5.5 items-center justify-center rounded-full bg-white leading-none text-neutral-600">
                            {Number(region?.id)}
                          </span>
                          {region?.name}
                        </div>
                        <Title
                          as="h3"
                          className={cn('h3 mb-md')}
                          data-map-content-el
                        >
                          {item.title}
                        </Title>
                        <Text
                          className={cn(
                            'textstyle-md-regular mb-lg max-sm:hidden'
                          )}
                          data-map-content-el
                        >
                          {item.text}
                        </Text>
                        <div data-map-content-el>
                          <Button
                            mode="invert"
                            href={item.button?.link?.href}
                            target={item.button?.link?.target}
                            onMouseEnter={() => handleMouseEnter(index)}
                            onMouseLeave={() => handleMouseLeave()}
                          >
                            {item.button?.label}
                          </Button>
                        </div>
                      </div>
                    </Container>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="absolute inset-0 opacity-0">
            {REGIONS.map((item) => (
              <button
                onFocus={() => {
                  setPrevRegionIndex(currentRegionIndex)
                  setCurrentRegionIndex(item.id)
                }}
                key={item.id?.toString()}
                aria-label={item.name}
                onClick={() => {
                  setPrevRegionIndex(currentRegionIndex)
                  setCurrentRegionIndex(item.id)
                }}
              >
                {item.name}
              </button>
            ))}
          </div>
          <ButtonIcon
            onClick={scrollNext}
            icon="material-symbols:arrow-forward-ios"
            className="right-sm max-md:bottom-sm absolute z-3 cursor-pointer md:top-2/3 md:-translate-y-1/2 lg:top-1/2"
            size="m"
            aria-label="Next slide"
          />
          <div className="lg:right-3xl lg:bottom-sm max-lg:left-md right-md max-sm:bottom-md bottom-sm relative z-2 flex aspect-343/292 items-center justify-center lg:absolute lg:top-1/2 lg:left-11/20 lg:-translate-y-1/2 2xl:left-1/2">
            <div className="relative max-lg:w-1/2 max-md:w-3/4 lg:col-start-2">
              {data.content.items?.map((item, index) => {
                const region = REGIONS.find(
                  (region) => region.id === Number(item.region)
                )
                return (
                  <SectionMapMarker
                    key={index?.toString()}
                    number={region?.id || 0}
                    left={region?.left || 0}
                    top={region?.top || 0}
                    name={region?.name || ''}
                    isCurrent={currentRegionIndex === Number(item.region)}
                  />
                )
              })}
              <svg
                width="546"
                height="465"
                className="h-auto w-full [&_path]:stroke-white [&_path]:stroke-3 [&_path]:transition-all"
                viewBox="0 0 546 465"
                fill="none"
              >
                {REGIONS.map((item) => (
                  <path
                    d={item.path}
                    stroke="white"
                    key={item.id?.toString()}
                    className={cn(
                      'data-[current=true]:fill-alpha-white-200 fill-transparent',
                      activeRegionsArray?.includes(item.id) &&
                        'hover:fill-alpha-white-200 cursor-pointer'
                    )}
                    data-id={item.id}
                    data-current={currentRegionIndex === item.id}
                    onClick={() => {
                      setPrevRegionIndex(currentRegionIndex)
                      if (activeRegionsArray?.includes(item.id)) {
                        setCurrentRegionIndex(item.id)
                      }
                    }}
                  />
                ))}
              </svg>
            </div>
          </div>
          <div
            ref={imageSlidesRef}
            className="pointer-events-none absolute inset-0 z-1 overflow-hidden"
          >
            <div className="absolute inset-0 z-10 bg-black/20" />
            {activeRegionsArray?.map((_, index) => (
              <div
                className={cn(
                  'absolute inset-0 h-full min-w-0 origin-[90%_50%] translate-0 pl-0'
                )}
                data-map-image-slide
                data-region={index}
                key={index?.toString()}
              >
                <Img
                  data={data.content.items[index].images[0]}
                  objectCover
                  naked={true}
                  width={1400}
                  height={800}
                  quality={60}
                  className={cn(
                    'image-anim absolute inset-0 h-full w-full',
                    isOver === index && 'image-anim-hover'
                  )}
                />
              </div>
            ))}
          </div>
        </div>
      </Section>
    </>
  )
}

SectionMap.displayName = 'SectionMap'

export default memo(SectionMap)
